import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { flushSync } from 'react-dom';
import { gameService } from '../services/gameService';
import GameSetupForm from '../components/HomePage/GameSetupForm';
import PageLayout from '../components/layout/PageLayout';
import DifficultyAddons from '../components/HomePage/DifficultyAddons';
import RankedModule from '../components/HomePage/RankedModule';
import styles from './HomePage.module.css';
import { useGameSettings } from '../contexts/GameSettingsContext';
import { calculateScoreMultiplier } from '../utils/scoreMultiplier';
import { getCollectionByMode } from '../constants/gameModes';
import { GameMode } from '../types/collection';
import { getRatingTags } from '../constants/rating';

const HomePage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const {
    gameMode,
    setGameMode,
    rating,
    setRating,
    artists,
    setArtists,
    rounds,
    setRounds,
    showFavorites,
    setShowFavorites,
    difficulty,
    setDifficulty,
    isBlurry,
    setIsBlurry,
    blurAmount,
    setBlurAmount,
    hasTimeLimit,
    setHasTimeLimit,
    timeLimitMs,
    setTimeLimitMs,
    isCropped,
    setIsCropped,
    cropPercentage,
    setCropPercentage,
    isFlipped,
    setIsFlipped,
    isInverted,
    setIsInverted,
    isMosaic,
    setIsMosaic,
    isNoColor,
    setIsNoColor,
    isRanked,
    setIsRanked,
    artistRangeStart,
    setArtistRangeStart,
    artistRangeEnd,
    setArtistRangeEnd,
  } = useGameSettings();

  // Get the current collection based on game mode
  const currentCollection = useMemo(() => getCollectionByMode(gameMode), [gameMode]);

  // Filter and display items based on current settings
  const filteredArtists = useMemo(() => {
    if (isRanked) {
      // Ranked mode: show all items filtered by range
      return currentCollection
        .map(item => item.name)
        .slice(artistRangeStart - 1, artistRangeEnd);
    }

    // Unranked mode: apply filters to show which items will be used
    let filtered = currentCollection.filter(item => artists.includes(item.name));

    // Apply favorites filter
    if (showFavorites) {
      filtered = filtered.filter(item => item.favorite);
    }

    // Apply difficulty filter
    if (difficulty !== null) {
      filtered = filtered.filter(item => item.difficulty === difficulty);
    }

    return filtered.map(item => item.name);
  }, [currentCollection, isRanked, artists, showFavorites, difficulty, artistRangeStart, artistRangeEnd]);

  const handleRankedChange = (value: boolean) => {
    flushSync(() => {
      setIsRanked(value);
      if (value) {
        // Reset to ranked defaults
        setGameMode('artist'); // Force game mode to artist collection in ranked mode
        const artistCollection = getCollectionByMode('artist');
        setRounds(10);
        setRating('all'); // Force rating to "All" in ranked mode
        setArtists(artistCollection.map(item => item.name));
        setShowFavorites(false);
        setDifficulty(null);
        // Reset range to full
        setArtistRangeStart(1);
        setArtistRangeEnd(artistCollection.length);
      }
    });
  };

  const handleGameModeChange = (mode: GameMode) => {
    const newCollection = getCollectionByMode(mode);
    flushSync(() => {
      setGameMode(mode);
      // Reset artists to the new collection
      setArtists(newCollection.map(item => item.name));
      // Reset range to full
      setArtistRangeStart(1);
      setArtistRangeEnd(newCollection.length);
      // Reset filters
      setShowFavorites(false);
      setDifficulty(null);
    });
  };

  const handleStartGame = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Use filtered artists for the game
    const artistsToUse = filteredArtists;

    if (artistsToUse.length === 0) {
      setError('No artists match the selected filters.');
      setIsLoading(false);
      return;
    }

    try {
      const ratingTags = getRatingTags(rating);
      const gameData = await gameService.startGame({
        selectedArtists: artistsToUse,
        numberOfRounds: rounds,
        ratingTags: ratingTags || undefined,
      });

      // Calculate full score multiplier including artist count
      let scoreMultiplier = 1.0;
      if (isRanked) {
        const difficultyMultiplier = calculateScoreMultiplier({
          isBlurry,
          blurAmount,
          hasTimeLimit,
          timeLimitMs,
          isCropped,
          cropPercentage,
          isFlipped,
          isInverted,
          isMosaic,
          isNoColor,
        });
        const artistCountMultiplier = artistsToUse.length / 100;
        scoreMultiplier = difficultyMultiplier * artistCountMultiplier;
      }

      navigate('/game', {
        state: {
          gameRounds: gameData,
          artists: artistsToUse,
          rounds,
          isRanked,
          scoreMultiplier,
          artistCount: artistsToUse.length,
          isBlurry,
          blurAmount,
          hasTimeLimit,
          timeLimitMs,
          isCropped,
          cropPercentage,
          isFlipped,
          isInverted,
          isMosaic,
          isNoColor,
        },
      });
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unknown error occurred.');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageLayout>
      <div className={styles.container}>
        <div className={styles.sidebar}>
          <RankedModule
            isRanked={isRanked}
            onIsRankedChange={handleRankedChange}
          />
        </div>
        <div className={styles.mainContent}>
          <GameSetupForm
            gameMode={gameMode}
            onGameModeChange={handleGameModeChange}
            rating={rating}
            onRatingChange={setRating}
            artists={filteredArtists.join(', ')}
            rounds={rounds}
            isLoading={isLoading}
            error={error}
            onArtistsChange={(value: string) => setArtists(value.split(/[\,\s]+/).map(a => a.trim()).filter(a => a))}
            onRoundsChange={setRounds}
            onStartGame={handleStartGame}
            showFavorites={showFavorites}
            onShowFavoritesChange={setShowFavorites}
            difficulty={difficulty}
            onDifficultyChange={setDifficulty}
            isRanked={isRanked}
          />
          <DifficultyAddons
            isBlurry={isBlurry}
            onIsBlurryChange={(value) => flushSync(() => setIsBlurry(value))}
            blurAmount={blurAmount}
            onBlurAmountChange={(value) => flushSync(() => setBlurAmount(value))}
            hasTimeLimit={hasTimeLimit}
            onHasTimeLimitChange={(value) => flushSync(() => setHasTimeLimit(value))}
            timeLimitMs={timeLimitMs}
            onTimeLimitMsChange={(value) => flushSync(() => setTimeLimitMs(value))}
            isCropped={isCropped}
            onIsCroppedChange={(value) => flushSync(() => setIsCropped(value))}
            cropPercentage={cropPercentage}
            onCropPercentageChange={(value) => flushSync(() => setCropPercentage(value))}
            isFlipped={isFlipped}
            onIsFlippedChange={(value) => flushSync(() => setIsFlipped(value))}
            isInverted={isInverted}
            onIsInvertedChange={(value) => flushSync(() => setIsInverted(value))}
            isMosaic={isMosaic}
            onIsMosaicChange={(value) => flushSync(() => setIsMosaic(value))}
            isNoColor={isNoColor}
            onIsNoColorChange={(value) => flushSync(() => setIsNoColor(value))}
          />
        </div>
      </div>
    </PageLayout>
  );
};

export default HomePage;
